<script setup lang="ts">
import { computed, defineEmits, onMounted, ref } from 'vue'
import { type FormInst, type FormRules, NAvatar, NForm, NFormItem, type UploadFileInfo, useMessage } from 'naive-ui'
import { t } from '@/locales'
import { getUserInfo, updateUserInfo } from '@/api/users'
import { transformMinioUrl } from '@/utils/functions'
import { useUserStore } from '@/store'
import { renderColor } from '@/composables/useRenderColor'
import { AppUpload, SvgIcon } from '@/components/common'
import { uploadFile } from '@/api/file'
import defaultAvatar from '@/assets/avatar.jpg'

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits(['onClose'])

const userStore = useUserStore()
const message = useMessage()

const showModal = ref(props.show)
const loading = ref(false)
const isEdit = ref<boolean>(false)
const userInfo = computed(() => userStore.userInfo)

const modelRef = ref<any>({
  fullName: userInfo.value.fullName || null,
  sdt: userInfo.value.sdt || null,
  address: userInfo.value.address || null,
  icon: userInfo.value.icon || null,
  icon_url: userInfo.value.icon_url || null,
  position: userInfo.value.position || null,
  email: userInfo.value.email || null,
})

const formRef = ref<FormInst | null>(null)

const rulesRef: FormRules = {
  fullName: [
    {
      required: true,
      message: t('user.userManagement.edit.validation.name'),
      trigger: ['blur', 'input', 'change'],
    },

  ],
  sdt: [
    {
      required: true,
      message: t('auth.validation.required_phone'),
      trigger: ['blur', 'input', 'change'],
    },
  ],
  email: [
    {
      required: true,
      message: t('auth.validation.required_email'),
      trigger: ['blur', 'input', 'change'],
    },
  ],
}

const listFile = ref<any[]>([])

const getDataUserInfo = async () => {
  try {
    const userId = userStore.userInfo?.userId || ''
    if (!userId)
      return

    const res = await getUserInfo({ user_id: userId })
    if (res?.success && res.data) {
      const update = {
        fullName: res.data.fullName || null,
        address: res.data.address || null,
        icon_url: res.data.icon_url || null,
        icon: res.data.icon || null,
        position: res.data.position || null,
        sdt: res.data.sdt || null,
      }
      userStore.updateUserInfo(update)
    }
  }
  catch (error) {
  }
}

const closeModal = (reload: boolean) => {
  showModal.value = false
  emit('onClose', reload)
}

const handleCancel = () => {
  closeModal(false)
}

const onUpdateProfile = async () => {
  try {
    loading.value = true
    const userId = userStore.userInfo?.userId || ''
    if (!userId) {
      message.error(t('auth.validation.required_account'))
      return
    }

    const payload = {
      fullName: modelRef.value.fullName,
      email: modelRef.value.email,
      sdt: modelRef.value.sdt || null,
      address: modelRef.value.address || null,
      icon: modelRef.value.icon || modelRef.value.icon_url || null,
      icon_url: modelRef.value.icon_url || modelRef.value.icon || null,
    }

    const res = await updateUserInfo(userId, payload)
    if (res?.success) {
      message.success(t('user.userManagement.edit.success'))
      await getDataUserInfo()
      closeModal(true)
    }
    else {
      message.error(t('user.userManagement.edit.error'))
    }
  }
  catch (e: any) {
    message.error(e?.response?.data?.message || t('auth.updateProfileFailed'))
  }
  finally {
    loading.value = false
  }
}

const handleSubmit = () => {
  formRef.value?.validate().then(async () => {
    await onUpdateProfile()
  }).catch((e: any) => {
    console.log('Validation errors:', e)
  }).finally(() => {})
}

const handleBeforeUpload = async (options: { file: UploadFileInfo; fileList: Array<UploadFileInfo> }) => {
  if (!isEdit.value)
    return false

  const file = options.file.file
  if (!file)
    return false

  try {
    const formData = {
      obj_type: 'user_avatar',
      description: 'User avatar image',
    }

    const response = await uploadFile({ file, data: formData })
    if (response?.success) {
      modelRef.value.icon = response.data.id
      modelRef.value.icon_url = response.data.url
    }
    else {
      message.error('Failed to upload avatar')
      modelRef.value.icon = modelRef.value.icon_url || null
    }
  }
  catch (error) {
    message.error('Failed to upload avatar')
    modelRef.value.icon = modelRef.value.icon_url || null
  }
}

const handleEdit = () => {
  isEdit.value = true
}

onMounted(async () => {
  await getDataUserInfo()
})
</script>

<template>
  <AppModal
    v-model:show="showModal"
    preset="card"
    transform-origin="center"
    style="width: 725px"
    class="!rounded-2xl"
    content-class="!px-4 !pt-4 !pb-0"
    header-class="!p-4"
    footer-class="!p-4 !w-full !pt-3 "
    :mask-closable="false"
    @after-leave="handleCancel"
    @esc="handleCancel"
  >
    <template #header>
      <div class="text-center text-2xl font-semibold text-gray-900 dark:text-neutral-100">
        {{ isEdit ? $t('auth.update_personal_information') : $t('auth.personal_information') }}
      </div>
    </template>

    <div class="w-full flex gap-3">
      <div class="flex justify-center mb-6 max-w-[88px]">
        <div v-if="isEdit" class="">
          <AppUpload
            :file-list="listFile"
            :multiple="false"
            accept="image/*"
            :show-file-list="false"
            :max-file-size="30"
            :show-loading="false"
            :show-upload-dragger="false"
            :show-preview-button="false"
            :show-remove-button="false"
            :show-download-button="false"
            class="w-full"
            @before-upload="handleBeforeUpload"
          >
            <div
              v-if="!modelRef.icon_url" class="relative min-w-20 w-20 h-20 rounded-full border flex items-center justify-center cursor-pointer" :style="{
                backgroundColor: renderColor(0.1),
              }"
            >
              <div class="">
                <div>
                  <SvgIcon icon="bi:image" size="20" class="text-[#6A5B70]" />
                </div>
                <div class="absolute -bottom-1 -right-1 min-w-6 w-6 h-6 border-[2px] border-white rounded-full bg-primary flex items-center justify-center">
                  <SvgIcon icon="ri:pencil-fill" size="14" class="text-white" />
                </div>
              </div>
            </div>

            <div
              v-else
              class="relative min-w-20 w-20 h-20 flex items-center justify-center rounded-full"
            >
              <NAvatar
                round
                class="w-full"
                :size="80"
                :src="transformMinioUrl(modelRef.icon_url) || defaultAvatar"
              />
              <div class="absolute -bottom-1 -right-1 min-w-6 w-6 h-6 border-[2px] border-white rounded-full bg-primary flex items-center justify-center">
                <SvgIcon icon="ri:pencil-fill" size="14" class="text-white" />
              </div>
            </div>
          </AppUpload>
        </div>
        <div v-else>
          <div
            class="min-w-20 w-20 h-20 flex items-center justify-center rounded-full"
          >
            <NAvatar
              round
              class="w-full"
              :size="80"
              :src="transformMinioUrl(modelRef.icon_url) || defaultAvatar"
            />
          </div>
        </div>
      </div>
      <div class="flex-1">
        <NForm ref="formRef" :model="modelRef" :rules="rulesRef" @keydown.enter.prevent="handleSubmit">
          <!-- Avatar -->

          <!-- Full Name -->
          <NFormItem
            :label="$t('user.userManagement.edit.name')" path="fullName" :rule="{
              required: true,
              message: $t('user.userManagement.edit.validation.name'),
              trigger: ['input', 'blur'],
            }"
          >
            <AppInput
              v-model:value="modelRef.fullName"
              :placeholder="$t('user.userManagement.edit.name')"
              size="medium"
              class=" !rounded-lg"
              :disabled="!isEdit"
            />
          </NFormItem>

          <!-- Email and Phone -->
          <div class="grid grid-cols-2 gap-3">
            <NFormItem :label="t('auth.email')" path="email">
              <AppInput
                v-model:value="modelRef.email"
                placeholder="email@example.com"
                size="medium"
                class=" "
                :disabled="true"
              />
            </NFormItem>
            <NFormItem :label="t('auth.phone')" path="phone">
              <AppInput
                v-model:value="modelRef.sdt"
                :placeholder="t('auth.phonePlaceholder')"
                size="medium"
                class=" "
                :disabled="!isEdit"
              />
            </NFormItem>
          </div>

          <!-- Address -->
          <NFormItem :label="t('auth.address')" path="address">
            <AppInput
              v-model:value="modelRef.address"
              :placeholder="t('auth.addressPlaceholder')"
              size="medium"
              class=" "
              :disabled="!isEdit"
            />
          </NFormItem>
        </NForm>
      </div>
    </div>

    <template #footer>
      <div v-if="isEdit" class="w-full flex gap-3 justify-center items-center">
        <AppButton
          ghost
          type="primary"
          class=" max-w-[180px] !w-full"
          size="medium"
          @click="handleCancel"
        >
          {{ $t('common.cancel') }}
        </AppButton>
        <AppButton
          type="primary"
          class=" max-w-[180px] !w-full"
          size="medium"
          :loading="loading"
          @click="handleSubmit"
        >
          {{ $t('common.save') }}
        </AppButton>
      </div>
      <div v-else class="w-full flex gap-3 justify-center items-center">
        <AppButton
          ghost
          type="primary"
          class=" max-w-[180px] !w-full"
          size="medium"
          @click="handleCancel"
        >
          {{ $t('auth.skip') }}
        </AppButton>
        <AppButton
          type="primary"
          class=" max-w-[180px] !w-full"
          size="medium"
          :loading="loading"
          @click="handleEdit"
        >
          {{ $t('auth.update') }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<style scoped>

</style>
